// Band-click detail modal — opened by clicking ANY band:
//  • a vehicle-lane band → keyed by its assignment; edits the drive legs.
//  • a Shipping / Rentals band → keyed by its event (no assignment); edits the
//    freetext transport note.
// Same compact two-column layout either way: event essentials + crew side-by-side,
// with the one editable bit folded into the transport section. Sized to fit
// without scrolling for a typical event.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate, getDisplayStatus } from '../utils/date-utils';
import useVehicleScheduleStore from '../stores/useVehicleScheduleStore';
import { VehicleAssignment } from './types';
import { CrewSection } from '../Events/EventClickPopup';
import { parseCityState, STATUS_COLORS } from '../Events/EventHoverCard';
import { buttonClass } from '../components/ui/Button';

interface Props {
  /** Vehicle-lane band: edit drive legs on this assignment. */
  assignmentId?: string | null;
  /** Shipping / Rentals band: edit transport notes on this event (no assignment). */
  eventId?: string | null;
  canEdit: boolean;
  onClose: () => void;
}

const inputClass =
  'w-full bg-gray-900 day:bg-white border border-gray-700 day:border-gray-300 rounded px-2.5 py-1.5 text-sm text-white day:text-gray-900 focus:border-primary focus:outline-none disabled:opacity-60';

const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-on-surface-variant day:text-gray-600 mb-1">{children}</div>
);

const LegEditorModal: React.FC<Props> = ({ assignmentId, eventId, canEdit, onClose }) => {
  const api = useApi();
  const toast = useToast();
  const assignments = useVehicleScheduleStore(s => s.assignments);
  const events = useVehicleScheduleStore(s => s.events);
  const vehicles = useVehicleScheduleStore(s => s.vehicles);
  const updateAssignment = useVehicleScheduleStore(s => s.updateAssignment);
  const setTransportLegs = useVehicleScheduleStore(s => s.setTransportLegs);

  const assignment = useMemo<VehicleAssignment | undefined>(
    () => (assignmentId ? assignments.find(a => a.id === assignmentId) : undefined),
    [assignments, assignmentId]
  );
  // Vehicle-band mode resolves the event via its assignment; rental-band mode
  // passes the eventId directly.
  const resolvedEventId = assignment?.event_id ?? eventId ?? null;
  const slimEvent = useMemo(() => events.find(e => e.id === resolvedEventId), [events, resolvedEventId]);
  const vehicle = useMemo(() => (assignment ? vehicles.find(v => v.id === assignment.vehicle_id) : undefined), [vehicles, assignment]);

  // Fetch the full event for show times + buffer (slim payload lacks them).
  const [fullEvent, setFullEvent] = useState<any>(null);
  useEffect(() => {
    let cancelled = false;
    if (!resolvedEventId) return;
    api.get(`/events/${resolvedEventId}`, { silent: true })
      .then((data: any) => { if (!cancelled) setFullEvent(data); })
      .catch(() => { /* fall back to slim */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedEventId]);

  // Assignment-mode fields (drive legs).
  const [originLabel, setOriginLabel] = useState(assignment?.origin_label ?? 'HQ');
  const [driveOutNote, setDriveOutNote] = useState(assignment?.drive_out_note ?? '');
  const [driveBackNote, setDriveBackNote] = useState(assignment?.drive_back_note ?? '');
  const [note, setNote] = useState(assignment?.note ?? '');
  // Event-mode fields (transport leg notes).
  const [outNote, setOutNote] = useState(slimEvent?.transport_out_note ?? '');
  const [backNote, setBackNote] = useState(slimEvent?.transport_back_note ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!assignment) return;
    setOriginLabel(assignment.origin_label ?? 'HQ');
    setDriveOutNote(assignment.drive_out_note ?? '');
    setDriveBackNote(assignment.drive_back_note ?? '');
    setNote(assignment.note ?? '');
  }, [assignment]);
  useEffect(() => {
    if (assignment) return;
    setOutNote(slimEvent?.transport_out_note ?? '');
    setBackNote(slimEvent?.transport_back_note ?? '');
  }, [slimEvent, assignment]);

  const handleSave = async () => {
    setSaving(true);
    let res: { ok: boolean; error?: string };
    if (assignment) {
      res = await updateAssignment(assignmentId as string, {
        origin_label: originLabel.trim() || null,
        drive_out_note: driveOutNote.trim() || null,
        drive_back_note: driveBackNote.trim() || null,
        note: note.trim() || null,
      });
    } else if (resolvedEventId) {
      res = await setTransportLegs(resolvedEventId, { out: outNote.trim() || null, back: backNote.trim() || null });
    } else {
      setSaving(false);
      return;
    }
    setSaving(false);
    if (res.ok) { toast.success('Saved'); onClose(); }
    else toast.error(res.error || 'Failed to save');
  };

  const e: any = fullEvent || slimEvent || {};
  const available = !!assignment || !!slimEvent;
  const vehicleName = vehicle?._extension?.name ?? vehicle?.display_code ?? 'Vehicle';
  const status = getDisplayStatus(e);
  const cityState = e.location ? parseCityState(e.location) : null;
  const shipping = e.transport_mode === 'shipping';

  const ls = e.ls_drone_count || 0;
  const py = e.pyro_drone_count || 0;
  const buf = e.buffer_percentage || 0;
  const droneTotal = ls + py;
  const buffered = ls + Math.ceil(ls * buf / 100) + py + Math.ceil(py * buf / 100);

  const dateText = (() => {
    if (!e.event_date) return '';
    const start = formatEventDate(e.event_date, 'ddd, MMM D, YYYY');
    if (e.end_date && e.end_date !== e.event_date) return `${start} → ${formatEventDate(e.end_date, 'MMM D')}`;
    return start;
  })();

  return (
    <div
      className="scrim z-[1000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 day:bg-white border border-gray-700/60 day:border-gray-200 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-700/50 day:border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[16px] font-bold leading-tight text-on-surface day:text-gray-900 break-words">{e.name || 'Event'}</h2>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ring-1 ring-inset ${STATUS_COLORS[status] || STATUS_COLORS.pending}`}>
                {status}
              </span>
              <span className="text-[11px] text-on-surface-variant day:text-gray-500">
                {assignment
                  ? <><span className="text-primary day:text-amber-700 font-medium">{vehicleName}</span> · drive legs</>
                  : <span className={shipping ? 'text-cyan-300 day:text-cyan-700 font-medium' : 'text-orange-300 day:text-orange-700 font-medium'}>{shipping ? 'Shipping' : 'Drive rental'}</span>}
              </span>
            </div>
          </div>
        </div>

        {!available && (
          <div className="px-5 py-6 text-sm text-on-surface-variant day:text-gray-500 text-center">This event is no longer available.</div>
        )}

        {available && (
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-[1.5fr_1fr] gap-x-6 gap-y-4">
            {/* ── Left: essentials + transport ── */}
            <div className="space-y-3.5 min-w-0">
              <div>
                <div className="text-[13px] font-semibold text-on-surface day:text-gray-900">{dateText}</div>
                <div className="text-[12px] text-on-surface-variant day:text-gray-600 mt-0.5 flex flex-wrap gap-x-3">
                  {e.show_time && <span>Show <span className="text-on-surface day:text-gray-800 font-medium">{e.show_time}</span>{e.timezone ? ` ${e.timezone}` : ''}</span>}
                  {e.is_double_show && e.second_show_time && <span>2nd <span className="text-on-surface day:text-gray-800 font-medium">{e.second_show_time}</span></span>}
                  {e.rain_date && <span className="text-amber-300/80 day:text-amber-600">Rain {formatEventDate(e.rain_date, 'MMM D')}</span>}
                </div>
              </div>

              {(droneTotal > 0 || e.tour_id) && (
                <div className="flex items-center gap-3 flex-wrap text-[12px]">
                  {droneTotal > 0 && (
                    <span>
                      <span className="text-[14px] font-bold text-primary day:text-amber-700 tabular-nums">{buffered}</span>
                      <span className="text-on-surface-variant day:text-gray-600 ml-1">drones</span>
                      {buffered !== droneTotal && <span className="text-[10px] text-on-surface-variant day:text-gray-500 ml-1">({droneTotal} + {buf}% buf)</span>}
                    </span>
                  )}
                  {e.tour_id && <span className="text-[11px] font-semibold uppercase tracking-wider text-cyan-300 day:text-cyan-600">• Tour</span>}
                </div>
              )}

              {e.location && (
                <div>
                  <div className="text-[12.5px] text-on-surface day:text-gray-800 break-words">{e.location}</div>
                  {cityState && <div className="text-[11px] text-on-surface-variant day:text-gray-500 mt-0.5">{cityState.city}, {cityState.state}</div>}
                </div>
              )}

              {assignment ? (
                /* Vehicle band — drive legs (assignment-owned) */
                <div className="border-t border-gray-700/40 day:border-gray-200 pt-3 space-y-2.5">
                  {/* Always the TRUCK's legs here, whatever the show's transport_mode
                      says: these inputs write event_vehicle.drive_*_note. A show can
                      carry a truck and shipped freight at once, and titling this
                      "Shipping" invited typing the freight's instructions into the
                      truck's note (the fallthrough band-runs.legNote exists to stop). */}
                  <SubLabel>Drive legs{canEdit ? '' : ' (view only)'}</SubLabel>
                  <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5">
                    <span className="text-[11px] text-on-surface-variant day:text-gray-500">Origin</span>
                    <input className={inputClass} value={originLabel} disabled={!canEdit} onChange={(ev) => setOriginLabel(ev.target.value)} placeholder="HQ" />

                    <span className="text-[11px] text-on-surface-variant day:text-gray-500 whitespace-nowrap">Out{e.ship_out_date ? ` · ${formatEventDate(e.ship_out_date, 'M/D')}` : ''}</span>
                    <input className={inputClass} value={driveOutNote} disabled={!canEdit} onChange={(ev) => setDriveOutNote(ev.target.value)} placeholder="e.g. 7 hr drive, leave 6am" />

                    <span className="text-[11px] text-on-surface-variant day:text-gray-500 whitespace-nowrap">Back{e.ship_back_date ? ` · ${formatEventDate(e.ship_back_date, 'M/D')}` : ''}</span>
                    <input className={inputClass} value={driveBackNote} disabled={!canEdit} onChange={(ev) => setDriveBackNote(ev.target.value)} placeholder="e.g. overnight then home" />

                    <span className="text-[11px] text-on-surface-variant day:text-gray-500">Note</span>
                    <input className={inputClass} value={note} disabled={!canEdit} onChange={(ev) => setNote(ev.target.value)} placeholder="anything else" />
                  </div>
                  <p className="text-[10px] text-on-surface-variant day:text-gray-500">Leave / return dates are set by dragging the band's edges (on a tour, those belong to the first and last stops), or on the event page.</p>
                </div>
              ) : (
                /* Shipping / Rentals band — outbound + return leg notes (event-owned) */
                <div className="border-t border-gray-700/40 day:border-gray-200 pt-3 space-y-2.5">
                  <SubLabel>{shipping ? 'Shipping legs' : 'Drive-rental legs'}{canEdit ? '' : ' (view only)'}</SubLabel>
                  <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5">
                    <span className="text-[11px] text-on-surface-variant day:text-gray-500 whitespace-nowrap">Out{e.ship_out_date ? ` · ${formatEventDate(e.ship_out_date, 'M/D')}` : ''}</span>
                    <input className={inputClass} value={outNote} disabled={!canEdit} onChange={(ev) => setOutNote(ev.target.value)} placeholder={shipping ? 'e.g. freight via R+L, picks up 6am' : 'e.g. 15ft box truck from Enterprise'} />

                    <span className="text-[11px] text-on-surface-variant day:text-gray-500 whitespace-nowrap">Back{e.ship_back_date ? ` · ${formatEventDate(e.ship_back_date, 'M/D')}` : ''}</span>
                    <input className={inputClass} value={backNote} disabled={!canEdit} onChange={(ev) => setBackNote(ev.target.value)} placeholder={shipping ? 'e.g. return freight, label in box' : 'e.g. drop rental Sun AM'} />
                  </div>
                  <p className="text-[10px] text-on-surface-variant day:text-gray-500">Ship-out / return dates are set by dragging the band's edges (on a tour, those belong to the first and last stops), or on the event page.</p>
                </div>
              )}
            </div>

            {/* ── Right: crew ── */}
            <div className="min-w-0 sm:border-l sm:border-gray-700/40 day:sm:border-gray-200 sm:pl-6">
              <CrewSection eventId={e.id} />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-700/50 day:border-gray-200 flex items-center justify-between gap-2">
          {resolvedEventId
            ? <Link to={`/events/${resolvedEventId}`} onClick={onClose} className="text-xs text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700 transition-colors">Open event page →</Link>
            : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={buttonClass('quiet')}>
              {canEdit ? 'Cancel' : 'Close'}
            </button>
            {available && canEdit && (
              <button onClick={handleSave} disabled={saving} className={buttonClass('filled')}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegEditorModal;
